import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom'
import Header from '../Component/Header'
import Footer from '../Component/footer'
import { canAccessManage, hasModule, isLoggedIn } from './auth'
import { needsPasswordChange, changePasswordPath } from '../utils/sessionAuth'
import { MANAGE_MODULES, MANAGE_NAV_SECTIONS, moduleByKey } from './modules'
import './Manage.css'

const SIDEBAR_STORAGE_KEY = 'manage_sidebar_sections'

function isItemActive(item, pathname) {
  if (item.exact) return pathname === item.path
  return pathname === item.path || pathname.startsWith(item.path + '/')
}

function sectionContainsPath(sectionItems, pathname) {
  return sectionItems.some((item) => isItemActive(item, pathname))
}

function readStoredOpen() {
  try {
    const raw = localStorage.getItem(SIDEBAR_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (e) {
    return null
  }
}

function buildNavSections() {
  const listedKeys = new Set(MANAGE_NAV_SECTIONS.flatMap((s) => s.keys))
  const orphanItems = MANAGE_MODULES.filter(
    (m) => !listedKeys.has(m.key) && hasModule(m.key)
  )

  const built = MANAGE_NAV_SECTIONS.map((section) => {
    const items = section.keys
      .map((key) => moduleByKey(key))
      .filter((m) => m && hasModule(m.key))
    if (section.id === 'settings' && orphanItems.length > 0) {
      return { ...section, items: [...items, ...orphanItems] }
    }
    return { ...section, items }
  }).filter((section) => section.items.length > 0)

  if (
    orphanItems.length > 0 &&
    !built.some((section) => section.id === 'settings')
  ) {
    built.push({
      id: 'settings',
      label: 'Settings',
      keys: [],
      items: orphanItems,
    })
  }

  return built
}

function initialOpenState(pathname) {
  const stored = readStoredOpen() || {}
  const open = { main: true, ...stored }
  MANAGE_NAV_SECTIONS.forEach((section) => {
    const items = section.keys.map((key) => moduleByKey(key)).filter(Boolean)
    if (sectionContainsPath(items, pathname)) {
      open[section.id] = true
    }
  })
  return open
}

function ManageLayout({ children }) {
  const history = useHistory()
  const location = useLocation()

  useEffect(() => {
    if (!isLoggedIn()) {
      history.replace('/Login')
      return
    }
    if (needsPasswordChange()) {
      history.replace(changePasswordPath('/manage'))
      return
    }
    if (!canAccessManage()) {
      history.replace('/')
    }
  }, [history, location.pathname])

  const displaySections = useMemo(() => buildNavSections(), [])

  const [openSections, setOpenSections] = useState(() =>
    initialOpenState(location.pathname)
  )

  // Keep the section for the current route expanded when navigating
  useEffect(() => {
    setOpenSections((prev) => {
      let changed = false
      const next = { ...prev }
      displaySections.forEach((section) => {
        if (
          sectionContainsPath(section.items, location.pathname) &&
          !next[section.id]
        ) {
          next[section.id] = true
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [location.pathname, displaySections])

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(openSections))
    } catch (e) {
      /* ignore quota / private mode */
    }
  }, [openSections])

  const toggleSection = useCallback((sectionId) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }))
  }, [])

  if (!isLoggedIn()) {
    return <Redirect to="/Login" />
  }
  if (needsPasswordChange()) {
    return <Redirect to={changePasswordPath('/manage')} />
  }
  if (!canAccessManage()) {
    return <Redirect to="/" />
  }

  return (
    <div className="manage-shell">
      <Header />
      <div className="container-fluid manage-body">
        <div className="row">
          <aside className="col-md-3 col-lg-2 manage-sidebar py-3">
            <div className="manage-sidebar-title px-2 mb-2">Manage</div>
            <nav className="manage-sidebar-nav" aria-label="Manage navigation">
              {displaySections.map((section) => {
                const isOpen = Boolean(openSections[section.id])
                const sectionActive = sectionContainsPath(
                  section.items,
                  location.pathname
                )
                return (
                  <div
                    key={section.id}
                    className={`manage-nav-section${sectionActive ? ' has-active' : ''}${isOpen ? ' is-open' : ''}`}
                  >
                    <button
                      type="button"
                      className="manage-nav-section-toggle"
                      onClick={() => toggleSection(section.id)}
                      aria-expanded={isOpen}
                      aria-controls={`manage-nav-${section.id}`}
                      id={`manage-nav-toggle-${section.id}`}
                    >
                      <span>{section.label}</span>
                      <span className="manage-nav-chevron" aria-hidden="true">
                        {isOpen ? '▾' : '▸'}
                      </span>
                    </button>
                    <div
                      id={`manage-nav-${section.id}`}
                      className="manage-nav-section-body"
                      role="region"
                      aria-labelledby={`manage-nav-toggle-${section.id}`}
                      hidden={!isOpen}
                    >
                      {section.items.map((item) => {
                        const active = isItemActive(item, location.pathname)
                        return (
                          <Link
                            key={item.key}
                            to={item.path}
                            className={`nav-link manage-nav-link${active ? ' active' : ''}`}
                          >
                            {item.label}
                          </Link>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </nav>
          </aside>
          <main className="col-md-9 col-lg-10 manage-main py-3">
            {children}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default ManageLayout
